import {Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular/ui/data-grid';
import {DxTextBoxComponent} from 'devextreme-angular/ui/text-box';
import {AjaxService} from 'AjaxService';
import {saveAs} from 'file-saver';
import {Subscription} from 'rxjs';
import {MitarbeiterAngaben, MitarbeiterNachweis, MitarbeiterNachweisUpload} from './mitarbeiter-personal.model';

interface NachweisZeile {
  key: string;
  name: string;
  dokumentart: string;
  gruppe: number;
  created?: Date;
  dokument?: MitarbeiterNachweis;
  upload?: MitarbeiterNachweisUpload;
}

@Component({
  selector: 'cz-mitarbeiter-nachweise',
  templateUrl: './mitarbeiter-nachweise.component.html',
  styleUrls: ['./mitarbeiter-nachweise.component.css'],
  standalone: false,
})
export class MitarbeiterNachweiseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: MitarbeiterAngaben;
  @Input() files: MitarbeiterNachweisUpload[] = [];
  @Output() filesChange = new EventEmitter<MitarbeiterNachweisUpload[]>();
  @Input() readOnly = false;
  @Input() disabled = false;
  @ViewChild(DxDataGridComponent) grid: DxDataGridComponent;
  @ViewChild('suche') suche: DxTextBoxComponent;
  documents: NachweisZeile[] = [];
  arten: {id: string; name: string}[] = [];
  uploadArt = 'nichtZugeordnet';
  filterArt: string = null;
  search = '';
  pickerFiles: File[] = [];
  narrow = window.innerWidth < 768;
  artenFehler = false;
  status = '';
  preview: NachweisZeile;
  previewVisible = false;
  previewUrl: string;
  private optionsSubscription: Subscription;
  private focusKey: string;

  constructor(private ajax: AjaxService) {}

  get valid(): boolean {
    return this.files.filter(item => !item.entfernt).every(item => this.validFile(item.file));
  }

  get removedCount(): number {
    return (this.model?.entfernteDokumentIds?.length ?? 0) + this.files.filter(item => item.entfernt).length;
  }

  ngOnInit() { this.loadArten(); }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['model']) {
      this.uploadArt = 'nichtZugeordnet';
      this.filterArt = null;
      this.search = '';
      this.pickerFiles = [];
      this.status = '';
      this.focusKey = null;
      this.closePreview();
      this.grid?.instance.expandAll();
    }
    this.updateDocuments();
  }

  loadArten() {
    this.artenFehler = false;
    this.optionsSubscription?.unsubscribe();
    this.optionsSubscription = this.ajax.get<{id: string; name: string}[]>('api/Options/PersonalNachweisarten').subscribe({
      next: arten => { this.arten = arten; this.updateDocuments(); },
      error: () => { this.artenFehler = true; }
    });
  }

  @HostListener('window:resize') onResize() { this.narrow = window.innerWidth < 768; }

  validFile(file: File): boolean {
    return /\.(pdf|jpe?g)$/i.test(file.name) && file.size > 0 && file.size <= 100 * 1024 * 1024;
  }

  artName(key: string): string { return this.arten.find(art => art.id === key)?.name ?? 'Noch nicht zugeordnet'; }
  groupName(rank: number): string { return this.arten[rank]?.name ?? 'Noch nicht zugeordnet'; }
  groupCount(rank: number): number { return this.documents.filter(row => row.gruppe === rank).length; }

  updateDocuments() {
    const existing = (this.model?.dokumente ?? []).filter(doc => !(this.model.entfernteDokumentIds ?? []).includes(doc.id))
      .map(doc => ({key: `dokument-${doc.id}`, name: doc.orgFileName || doc.bezeichnung,
        dokumentart: this.model.nachweisArtAenderungen?.find(item => item.dokumentId === doc.id)?.dokumentart ?? doc.dokumentart ?? 'nichtZugeordnet',
        created: doc.created, dokument: doc}));
    const pending = this.files.filter(item => !item.entfernt).map(item => ({key: item.key, name: item.file.name, dokumentart: item.dokumentart, upload: item}));
    this.documents = [...existing, ...pending]
      .filter(row => (!this.filterArt || row.dokumentart === this.filterArt) && row.name.toLocaleLowerCase('de').includes((this.search ?? '').trim().toLocaleLowerCase('de')))
      .map(row => ({...row, gruppe: this.arten.findIndex(art => art.id === row.dokumentart)}));
  }

  selectFiles(files: File[]) {
    if (this.readOnly || this.disabled || !files?.length) return;
    this.files = [...this.files, ...files.map(file => ({key: `upload-${crypto.randomUUID()}`, file, dokumentart: this.uploadArt}))];
    this.filesChange.emit(this.files);
    this.pickerFiles = [];
    this.updateDocuments();
    this.grid?.instance.expandRow([this.arten.findIndex(art => art.id === this.uploadArt)]);
    this.status = `${files.length} Datei(en) hinzugefügt. Die Änderungen werden mit dem Mitarbeiter gespeichert.`;
  }

  changeArt(row: NachweisZeile, event: {value: string; event?: unknown}) {
    if (!event.event || this.readOnly || this.disabled || row.dokumentart === event.value) return;
    if (row.upload) {
      row.upload.dokumentart = event.value;
      this.filesChange.emit(this.files);
    } else {
      const changes = (this.model.nachweisArtAenderungen ?? []).filter(item => item.dokumentId !== row.dokument.id);
      if (event.value !== (row.dokument.dokumentart ?? 'nichtZugeordnet')) changes.push({dokumentId: row.dokument.id, dokumentart: event.value});
      this.model.nachweisArtAenderungen = changes;
    }
    this.focusKey = row.key;
    this.updateDocuments();
    this.grid?.instance.expandRow([this.arten.findIndex(art => art.id === event.value)]);
    this.status = `${row.name}: ${this.artName(event.value)}. Noch nicht gespeichert.`;
  }

  onContentReady() {
    if (!this.focusKey) return;
    const key = this.focusKey;
    // Die Angular-Zellvorlagen stehen erst nach dem Grid-Ereignis vollständig bereit.
    setTimeout(() => {
      if (this.focusKey !== key) return;
      this.focusKey = null;
      const index = this.grid?.instance.getRowIndexByKey(key);
      const row = index >= 0 ? this.grid.instance.getRowElement(index)?.[0] as HTMLElement : null;
      const editor = row?.querySelector<HTMLElement>('[role="combobox"]');
      if (editor) editor.focus();
      else this.suche?.instance.focus();
    });
  }

  remove(row: NachweisZeile) {
    if (this.readOnly || this.disabled) return;
    if (row.upload) { row.upload.entfernt = true; this.filesChange.emit(this.files); }
    else this.model.entfernteDokumentIds = [...(this.model.entfernteDokumentIds ?? []), row.dokument.id];
    this.updateDocuments();
    this.status = `${row.name} wird beim Speichern entfernt. Die Entfernung kann rückgängig gemacht werden.`;
  }

  undoRemove() {
    if (this.readOnly || this.disabled) return;
    this.model.entfernteDokumentIds = [];
    this.files.forEach(item => item.entfernt = false);
    this.filesChange.emit(this.files);
    this.updateDocuments();
    this.grid?.instance.expandAll();
    this.status = 'Alle vorgemerkten Entfernungen wurden rückgängig gemacht.';
  }

  download(row: NachweisZeile) {
    if (row.upload) saveAs(row.upload.file, row.name);
    else this.ajax.download(`api/Dokument/Download?fileName=${encodeURIComponent(row.dokument.fileName)}&dokumentName=${encodeURIComponent(row.dokument.bezeichnung)}`, row.name);
  }

  openPreview(row: NachweisZeile) {
    this.closePreview();
    this.preview = row;
    if (row.upload) this.previewUrl = URL.createObjectURL(row.upload.file);
    this.previewVisible = true;
  }

  closePreview() {
    this.previewVisible = false;
    this.preview = null;
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  ngOnDestroy() { this.focusKey = null; this.optionsSubscription?.unsubscribe(); this.closePreview(); }
}
